// src/hr/portal/LogTimeOffDrawer.js -- HR's "Log time off" (mockup screen 2), also used to edit an entry.
// Every live type is offered, protected leave included: protected leave never touches a balance, so it
// shows "Doesn't use any balance" instead of a preview. Sick & Safe never takes a note. The preview is
// a client-side estimate (weekdays minus company holidays); the server recounts and has the last word.
// A refusal keeps the drawer open with the server's own message.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiCheck, FiInfo, FiShield } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Drawer, EmptyState, Field, Pill, Seg, Skeleton } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel, daysNumber, dayLabel } from '../format';
import { isDateString, isWeekend, nextWorkday } from '../timeoff/dates';
import { estimateHours, typeLabel, typeTone } from '../timeoff/labels';
import { fullName } from './portalUtils';

const LENGTHS = [{ value: 8, label: 'Full day' }, { value: 4, label: 'Half day' }];
const PROTECTED = '__protected';
// More choices than this and the type picker becomes a list instead of a segmented control.
const MAX_SEGMENTS = 4;

const bySortOrder = (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) || String(a.name).localeCompare(String(b.name));
const notesAllowed = (type) => !!type && type.allowNotes !== false && !type.isSick;

function startDay(today, holidays) {
  return isWeekend(today) || holidays.has(today) ? nextWorkday(today, holidays) : today;
}

function TypePicker({ types, typeId, onChange, labelId, fixed }) {
  const balanceTypes = types.filter((t) => !t.protected);
  const protectedTypes = types.filter((t) => t.protected);
  const current = types.find((t) => t.id === typeId) || null;
  const whichId = `${labelId}-which`;

  if (fixed) {
    return current ? <Pill {...typeTone(current)} dot>{typeLabel(current)}</Pill> : null;
  }

  const segments = balanceTypes.length + (protectedTypes.length ? 1 : 0);
  if (segments > MAX_SEGMENTS) {
    return (
      <select className="hrp-select" aria-labelledby={labelId} value={typeId} onChange={(e) => onChange(e.target.value)}>
        <optgroup label="Uses a balance">
          {balanceTypes.map((t) => <option key={t.id} value={t.id}>{typeLabel(t)}</option>)}
        </optgroup>
        {protectedTypes.length > 0 && (
          <optgroup label="Protected leave · no balance">
            {protectedTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </optgroup>
        )}
      </select>
    );
  }

  const options = balanceTypes.map((t) => ({ value: t.id, label: typeLabel(t) }));
  if (protectedTypes.length) options.push({ value: PROTECTED, label: 'Protected leave' });
  const segValue = current && current.protected ? PROTECTED : typeId;
  return (
    <>
      <Seg
        labelledBy={labelId}
        className="hrp-ltd__types"
        options={options}
        value={segValue}
        onChange={(value) => onChange(value === PROTECTED ? protectedTypes[0].id : value)}
      />
      {current && current.protected && (
        <Field
          label="Which leave"
          type="select"
          id={whichId}
          className="hrp-ltd__which"
          value={typeId}
          options={protectedTypes.map((t) => ({ value: t.id, label: t.name }))}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </>
  );
}

function LogForm({ person, entry, types: allTypes, holidays: holidayList, yearRange, today, context, onClose, onDone }) {
  const editing = !!entry;
  const holidays = useMemo(() => new Set((holidayList || []).map((h) => h.date)), [holidayList]);
  const types = useMemo(() => (allTypes || []).filter((t) => !t.archived || (entry && t.id === entry.typeId)).sort(bySortOrder), [allTypes, entry]);
  const firstDay = startDay(today, holidays);
  const defaultType = types.find((t) => t.key === 'pto') || types.find((t) => !t.protected) || types[0] || null;

  const [typeId, setTypeId] = useState(entry ? entry.typeId : (defaultType ? defaultType.id : ''));
  const [startDate, setStartDate] = useState(entry ? entry.startDate : firstDay);
  const [endDate, setEndDate] = useState(entry ? entry.endDate : firstDay);
  const [hoursPerDay, setHoursPerDay] = useState(entry ? Number(entry.hoursPerDay) || 8 : 8);
  const [note, setNote] = useState(entry && entry.note ? entry.note : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const ids = `hrp-ltd-${plainId(useId())}`;
  const errorRef = useRef(null);

  // Types arrive after the drawer opens when the app is still loading them.
  useEffect(() => {
    if (!typeId && defaultType) setTypeId(defaultType.id);
  }, [typeId, defaultType]);

  useEffect(() => {
    if (error && errorRef.current && errorRef.current.scrollIntoView) errorRef.current.scrollIntoView({ block: 'nearest' });
  }, [error]);

  const type = types.find((t) => t.id === typeId) || null;
  const name = fullName(person);
  const balance = ((person && person.balances) || []).find((b) => b.typeId === typeId) || null;
  const datesOk = isDateString(startDate) && isDateString(endDate);
  const ordered = datesOk && endDate >= startDate;
  const hours = ordered ? estimateHours(startDate, endDate, hoursPerDay, holidays) : 0;
  const inThisYear = (date) => !yearRange || (date >= yearRange.start && date <= yearRange.end);
  const otherYear = ordered && !inThisYear(startDate);
  // An entry being edited is already counted in the balance; its hours come back first.
  const ownHours = editing && entry.typeId === typeId && inThisYear(entry.startDate) ? Number(entry.totalHours) || 0 : 0;
  const before = balance ? (Number(balance.availableHours) || 0) + ownHours : null;
  const after = before === null ? null : before - hours;
  const holidaysInRange = ordered ? (holidayList || []).filter((h) => h.date >= startDate && h.date <= endDate) : [];
  const ptoBalance = ((person && person.balances) || []).find((b) => b.key === 'pto');

  const changeStart = (value) => {
    setStartDate(value);
    if (isDateString(value) && (!isDateString(endDate) || endDate < value)) setEndDate(value);
  };
  // A protected-leave note is HR-only while a PTO note is shown to the employee, so a note never
  // carries across between the two.
  const changeType = (value) => {
    const next = types.find((t) => t.id === value);
    if (type && next && !!type.protected !== !!next.protected) setNote('');
    setTypeId(value);
    setError('');
  };

  const submit = async () => {
    if (busy || !type) return;
    if (!ordered) {
      setError(datesOk ? 'The last day is before the first day' : 'Pick valid dates');
      return;
    }
    const trimmed = note.trim();
    let body;
    if (editing) {
      body = {};
      if (startDate !== entry.startDate) body.startDate = startDate;
      if (endDate !== entry.endDate) body.endDate = endDate;
      if (hoursPerDay !== Number(entry.hoursPerDay)) body.hoursPerDay = hoursPerDay;
      if (notesAllowed(type) && trimmed !== (entry.note || '')) body.note = trimmed;
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
    } else {
      body = { typeId, startDate, endDate, hoursPerDay };
      if (notesAllowed(type) && trimmed) body.note = trimmed;
    }
    setBusy(true);
    setError('');
    try {
      const result = editing ? await hr.admin.entries.update(entry.id, body) : await hr.admin.entries.create(person.id, body);
      const saved = (result && result.entry) || null;
      // The server's own count when it sent one (it knows every holiday); otherwise the estimate.
      const logged = saved && typeof saved.totalHours === 'number' ? saved.totalHours : hours;
      onDone({
        entry: saved,
        text: editing ? 'Entry updated' : `Logged ${daysLabel(logged)} of ${typeLabel(type)} for ${name}`
      });
    } catch (err) {
      setError(errorText(err, editing ? "Couldn't save the entry. Try again." : "Couldn't log that time off. Try again."));
      setBusy(false);
    }
  };

  const ready = types.length > 0;
  const footer = ready ? (
    <>
      <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      <Btn type="submit" icon={FiCheck} loading={busy}>{editing ? 'Save changes' : 'Save entry'}</Btn>
    </>
  ) : <Btn variant="ghost" onClick={onClose}>Close</Btn>;

  let content;
  if (!ready && context && context.status === 'error') {
    content = (
      <EmptyState compact icon={FiAlertCircle} title="Couldn't load the time-off types" action={<Btn variant="ghost" size="sm" onClick={context.reload}>Try again</Btn>}>
        Something went wrong on our side.
      </EmptyState>
    );
  } else if (!ready) {
    content = <Skeleton label="Loading time-off types" lines={4} height={14} />;
  } else {
    content = (
      <>
        <div className="hrp-ltd__who">
          <Avatar id={person.id} name={name} size="md" />
          <div className="hrp-ltd__whotxt">
            <b>{name}</b>
            <span>
              {[person.department, ptoBalance && ptoBalance.totalHours > 0 ? `PTO ${daysNumber(ptoBalance.availableHours)} left` : null]
                .filter(Boolean).join(' · ') || 'No balances set up yet'}
            </span>
          </div>
        </div>

        <div className="hrp-field">
          <div className="hrp-field__label" id={`${ids}-type`}>Type</div>
          <TypePicker types={types} typeId={typeId} onChange={changeType} labelId={`${ids}-type`} fixed={editing} />
        </div>

        <div className="hrp-two">
          <Field label="First day" type="date" value={startDate} onChange={(e) => changeStart(e.target.value)} required />
          <Field label="Last day" type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} required />
        </div>

        <div className="hrp-field">
          <div className="hrp-field__label" id={`${ids}-len`}>Length per day</div>
          <Seg labelledBy={`${ids}-len`} options={LENGTHS} value={hoursPerDay} onChange={setHoursPerDay} />
        </div>

        {notesAllowed(type) && (
          <Field
            label="Note"
            labelNote={type.protected ? '(optional · HR only)' : '(optional · they see it)'}
            value={note}
            maxLength={200}
            placeholder={type.protected ? 'e.g. Court summons' : 'e.g. Holiday travel'}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
        {type && type.isSick && (
          <p className="hrp-ltd__info"><FiInfo aria-hidden="true" />Sick &amp; Safe never stores a reason — just the dates.</p>
        )}

        {type && type.protected ? (
          <div className="hrp-ltd__prot" data-testid="hrp-ltd-preview" aria-live="polite">
            <span className="hrp-ltd__protic" aria-hidden="true"><FiShield /></span>
            <div>
              <div className="hrp-ltd__prott">Doesn't use any balance</div>
              <p className="hrp-req__note">Kept for HR's records. It shows as “Out” on Who's Out and is never emailed to anyone but HR.</p>
              {ordered && (
                <p className="hrp-req__note">
                  {hours > 0 ? `${daysLabel(hours)} of leave · weekends and holidays aren’t counted` : 'Those dates have no workdays'}
                </p>
              )}
              {!ordered && <p className="hrp-req__warn">{datesOk ? 'The last day is before the first day.' : 'Pick the first and last day.'}</p>}
            </div>
          </div>
        ) : (
          <div className="hrp-req__prev" data-testid="hrp-ltd-preview" aria-live="polite">
            <div className="hrp-lab hrp-req__prevlab">{`${typeLabel(type)} balance after this`}</div>
            {!ordered && <p className="hrp-req__warn">{datesOk ? 'The last day is before the first day.' : 'Pick the first and last day.'}</p>}
            {ordered && otherYear && (
              <p className="hrp-req__note">These dates fall outside the current benefit year, so this year's balance isn't affected.</p>
            )}
            {ordered && !otherYear && after !== null && (
              <div className="hrp-req__math">
                <span>{daysNumber(before)}</span>
                <span className="hrp-req__arrow" aria-hidden="true">→</span>
                <span className="hrp-sr">becomes</span>
                <b data-neg={after < 0 ? 'true' : undefined}>{daysNumber(after)}</b>
                <span>days left</span>
              </div>
            )}
            {ordered && !otherYear && after === null && <p className="hrp-req__note">No balance set up for this type yet.</p>}
            {ordered && (
              <p className="hrp-req__note">
                {hours > 0 ? `${daysLabel(hours)}` : 'Those dates have no workdays'}
                {hours > 0 && ' · weekends and holidays aren’t counted'}
              </p>
            )}
            {holidaysInRange.length > 0 && (
              <p className="hrp-req__note">
                {`Includes ${holidaysInRange.map((h) => `${h.name} (${dayLabel(h.date)})`).join(', ')} — not counted.`}
              </p>
            )}
            {ordered && !otherYear && after !== null && after < 0 && (
              <p className="hrp-req__warn">That takes them below zero. You can still save it.</p>
            )}
          </div>
        )}

        {error && (
          <div ref={errorRef} className="hrp-form-err" role="alert">
            <FiAlertCircle aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
      </>
    );
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing ? 'Edit entry' : 'Log time off'}
      width={400}
      footer={footer}
      onSubmit={submit}
      className="hrp-ltd"
    >
      {content}
    </Drawer>
  );
}

// The form is mounted fresh each time the drawer opens, so it always starts from the defaults.
export function LogTimeOffDrawer({ open, ...props }) {
  if (!open || !props.person) return null;
  return <LogForm {...props} />;
}

export default LogTimeOffDrawer;
